import PageComponent from 'components/PublicProfilePage'
import type { GetStaticPropsContext, NextPage } from 'next'
import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import type { SSRData } from 'components/PublicProfilePage/types'
import request, { GraphQLClient } from 'graphql-request'
import { env } from 'env/server.mjs'
import {
  BasicUsersDataByUsernameDocument,
  BasicUsersDataByUsernameQuery,
  GetAllInfluencersThatAreReadyDocument,
  GetAllInfluencersThatAreReadyQuery,
  PublicProfileDocument,
  PublicProfileQuery,
} from 'generated/graphql'
import { sortBy } from 'lodash'
import friendlyAmount from 'utils/friendlyAmount'
import serverSideTranslationsWithDefaultNS from 'utils/serverSideTranslationsWithDefaultNS'
import { PlatformBrand } from 'types/next-auth'

export const getStaticProps = async (context: GetStaticPropsContext) => {
  try {
    // NOTE: This code cannot be reused, changes need to be copied and shared between /user/[username] and /[username]
    const getPublicProfileData = async (username: string): Promise<SSRData> => {
      const { WUI_User } = await request<BasicUsersDataByUsernameQuery>({
        url: env.NEXT_PUBLIC_GRAPHQL_URL,
        requestHeaders: {
          'x-hasura-admin-secret': env.HASURA_ADMIN_SECRET,
        },
        document: BasicUsersDataByUsernameDocument,
        variables: {
          username,
        },
      })

      if (!Array.isArray(WUI_User) || !WUI_User[0]) {
        throw new Error(`User not found`)
      }

      const profile = WUI_User[0]

      // NOTE: This code cannot be used, changes need to be copied and shared between /user/[username] and /[username]
      const {
        SocialMediaDemographic: socialMediaDemographic,
        WUI_SellerPackage: packages,
        WUI_UserFilePortfolio,
        SocialMediaLink: socialMediaLinks,
        WUI_UserReview_aggregate,
        UserVideo,
        WUI_Affiliate_aggregate,
        WUI_UserSubscription_aggregate,
        VoteUser_by_pk,
      } = await request<PublicProfileQuery>({
        url: env.NEXT_PUBLIC_GRAPHQL_URL,
        requestHeaders: {
          'x-hasura-admin-secret': env.HASURA_ADMIN_SECRET,
        },
        document: PublicProfileDocument,
        variables: {
          userID: profile.id,
        },
      })

      // NOTE: This code cannot be used, changes need to be copied and shared between /user/[username] and /[username]
      return {
        platform: profile.platform_brand as PlatformBrand, // Note: this is needed for the Footer Logo, do not remove

        profile,
        socialMediaDemographic,

        packages,
        portfolioPictures: sortBy(WUI_UserFilePortfolio, 'order')
          .map((picture) => picture?.WUI_File?.url)
          .filter(Boolean) as string[],
        socialMediaLinks,
        reviewAvg: {
          value: Math.round(
            WUI_UserReview_aggregate.aggregate?.avg?.stars || 0,
          ),
          friendlyValue: friendlyAmount(
            WUI_UserReview_aggregate.aggregate?.avg?.stars || 0,
          ),
          count: WUI_UserReview_aggregate.aggregate?.count || 0,
        },
        videos: UserVideo,
        isAffiliate:
          !!WUI_Affiliate_aggregate?.aggregate?.count &&
          WUI_Affiliate_aggregate.aggregate.count > 0,
        isVerified:
          !!WUI_UserSubscription_aggregate?.aggregate?.count &&
          WUI_UserSubscription_aggregate.aggregate.count > 0,
        votes:
          VoteUser_by_pk &&
          VoteUser_by_pk.VoteUserCategories.length > 0 &&
          VoteUser_by_pk.VoteUserCategories.some(
            ({ category_id }) =>
              category_id === VoteUser_by_pk.primary_category_id,
          )
            ? {
                voteUserCategoryId: VoteUser_by_pk.VoteUserCategories.find(
                  ({ category_id }) =>
                    category_id === VoteUser_by_pk.primary_category_id,
                )?.id,
                primaryCategoryId: VoteUser_by_pk.primary_category_id,
                startsAt: VoteUser_by_pk.started_at,
                // startsAt: '2023-04-08T19:57:00.136Z',
                endsAt: VoteUser_by_pk.ended_at,
                // endsAt: '2023-04-09T19:48:39.136Z',
              }
            : null,
      }
    }
    // NOTE: This code cannot be used, changes need to be copied and shared between /user/[username] and /[username]

    const username = Array.isArray(context.params?.username)
      ? context.params?.username[0]
      : context.params?.username

    if (!username) {
      return {
        notFound: true,
      }
    }

    const data = await getPublicProfileData(username)

    return {
      props: {
        ...data,
        ...(context.locale
          ? await serverSideTranslationsWithDefaultNS(context.locale, [
              'vote',
              'voteCategory',
              'reviews',
            ])
          : undefined),
        ssr: true,
      },

      // Next.js will attempt to re-generate the page:
      // - When a request comes in
      // - At most once every 10 seconds
      revalidate: 60 * 60, // In seconds
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error)

    return {
      notFound: true,
    }
  }
}

export async function getStaticPaths() {
  if (env.SKIP_BUILD_STATIC_GENERATION) {
    return {
      paths: [],
      fallback: 'blocking',
    }
  }

  const client = new GraphQLClient(env.NEXT_PUBLIC_GRAPHQL_URL, {
    headers: {
      'x-hasura-admin-secret': env.HASURA_ADMIN_SECRET,
    },
  })

  // TODO: this gets ALL users, we need pagination
  const { WUI_User } = await client.request<GetAllInfluencersThatAreReadyQuery>(
    GetAllInfluencersThatAreReadyDocument,
  )

  const paths = WUI_User.map(({ username }) => ({
    params: { username },
  }))

  const pathsWithLocales = [
    ...paths.map((path) => ({ ...path, locale: 'en' })),
    ...paths.map((path) => ({ ...path, locale: 'es' })),
  ]

  return {
    paths: pathsWithLocales,
    // 'true': prerenders in the background
    // 'false': means other routes should 404
    // 'blocking': don't prerender any static pages

    // We'll pre-render only these paths at build time.
    // { fallback: 'blocking' } will server-render pages
    // on-demand if the path doesn't exist.
    fallback: 'blocking',
  }
}

const PublicProfile: NextPage<SSRData> = (props) => {
  const router = useRouter()
  const session = useSession()

  useEffect(() => {
    // This means that the page could be cached, we need to re-validate
    if (session.status === 'unauthenticated') {
      fetch('/api/auth/session')
        .then((r) => r.json())
        .then((apiSession) => {
          if (apiSession?.user) {
            window.location.href = `/user${router.asPath}`
          }
        })
    }
  }, [])

  if (router.isFallback) return <div>Loading...</div>

  return <PageComponent {...(props as any)} />
}

;(PublicProfile as any).skipCompleteProfile = true

export default PublicProfile
